import PropTypes from 'prop-types';
import React, { useState } from 'react';
import Select, { components } from 'react-select';
import { useTranslation } from 'react-i18next';

import { IconDropDownMo, IconWarning } from 'svgIcons/MotionPortalIcons';

import colors from 'theme/_colors.scss';
import './styles.scss';

const colourStyles = {
    container: (styles, { isDisabled }) => ({
        ...styles,
        pointerEvents: isDisabled ? 'auto' : styles?.pointerEvents,
    }),
    control: (styles, { isDisabled, selectProps: { error } }) => {
        return {
            ...styles,
            backgroundColor: isDisabled ? colors.alto : colors.white,
            borderColor: error ? colors.red : styles.borderColor,
            cursor: isDisabled ? 'not-allowed' : 'default',
            '&:hover': {
                borderColor: error ? colors.red : styles['&:hover']?.borderColor,
                cursor: isDisabled ? 'not-allowed' : 'default',
            },
        };
    },
    option: (styles, { isDisabled, isFocused, data, isSelected }) => ({
        ...styles,
        fontWeight: data.bold ? 'bold' : 'normal',
        backgroundColor: isDisabled ? null : isSelected ? colors.lightBlue : isFocused ? colors.concrete : colors.transparent,
        color: isDisabled ? colors.gallery : colors.codGray,
        cursor: isDisabled ? 'not-allowed' : 'default',
        ':active': {
            ...styles[':active'],
            backgroundColor: !isDisabled && colors.transparent,
        },
    }),
    menuPortal: (styles) => ({
        ...styles,
        top: 'auto'
    })
};

const ValueContainer = ({ children, ...props }) => {
    const { getValue, hasValue, isMulti } = props;
    const selectionsLength = getValue().length;
    return (
        isMulti ? <components.ValueContainer {...props}>
            {React.Children.map(children, (child) => {
                return !hasValue || child.type === components.Input ? child : null;
            })}
            {
                hasValue && (!props.selectProps.inputValue || props.selectProps.inputValue.length === 0) ?
                    <div className='selected-number'>{`${selectionsLength} selected`}</div>
                    : null
            }
        </components.ValueContainer>
            : <components.ValueContainer {...props}>{children}</components.ValueContainer>
    );
};

const DropdownIndicator = (props) => {
    if (props?.isDisabled) {
        return null;
    }
    return (
        <components.DropdownIndicator {...props}>
            <IconDropDownMo color={colors.doveGray} />
        </components.DropdownIndicator>
    );
};

const ErrorIndicator = () => {
    return (
        <div className='error-indicator'>
            <IconWarning color={colors.red} />
        </div>
    );
};

const INPUT_CHAR_MAX = 50;

const CheckboxOption = (optionProps) => {
    const { isSelected, selectProps } = optionProps;
    const { isMulti } = selectProps;

    return (
        <components.Option {...optionProps}>
            {isMulti &&
                <input
                    type='checkbox'
                    checked={isSelected}
                />
            }
            <span className='option-label'>{optionProps.children}</span>
        </components.Option>
    );
};

const GeneralSelectComponent = ({ showErrorIndicator = true, leftIcon, ...props }) => {
    const searchInputCharMax = props.inputMaxChars || INPUT_CHAR_MAX;
    const { t: translate } = useTranslation();
    const [inputValue, setInputValue] = useState('');
    const [selectedOption, setSelectedOption] = useState(null);

    const onInputChange = (query, action) => {
        // Prevents resetting our input after option has been selected
        if (action.action !== 'set-value' && query?.length <= searchInputCharMax) {
            setInputValue(query);
            if (props.onInputChange) {
                props.onInputChange(query, action);
            }
        }
    };

    const onChange = (selected, event) => {
        setSelectedOption(selected);
        if (props.onChange) {
            props.onChange(selected, event);
        }
    };

    const customComponents = {
        ValueContainer, IndicatorSeparator: () => null, DropdownIndicator, Option: CheckboxOption
    };

    return (
        <div className={`general-select-wrapper ${props.error ? 'has-error' : ''} ${leftIcon ? 'with-left-icon' : ''}`}>
            {leftIcon && <div className='left-icon'>{leftIcon}</div>}
            {props.disableOnSelect && selectedOption || props.selectedValue ?
                <div className='selected-value'>
                    {props.selectedValue || selectedOption?.title}
                </div>
                :
                <Select
                    {...props}
                    components={{ ...customComponents, ...props.components }}
                    styles={colourStyles}
                    onInputChange={onInputChange}
                    inputValue={inputValue}
                    onChange={onChange}
                    closeMenuOnSelect={!props.isMulti}
                    isClearable={props.isClearable ?? false}
                    noOptionsMessage={props.noOptionsMessage || (() => translate('Abb.Powertrain.Frontend.lblNoOptions'))}
                    className={'general-select-container ' + (props.className ? props.className : '')}
                    classNamePrefix='general-select'
                />
            }
            {props.error && showErrorIndicator && <ErrorIndicator />}
        </div>
    );
};

GeneralSelectComponent.propTypes = {
    className: PropTypes.string,
    components: PropTypes.any,
    error: PropTypes.bool,
    disableOnSelect: PropTypes.bool,
    isMulti: PropTypes.bool,
    noOptionsMessage: PropTypes.func,
    onInputChange: PropTypes.func,
    onChange: PropTypes.func,
    selectedValue: PropTypes.string,
    showErrorIndicator: PropTypes.bool,
    leftIcon: PropTypes.node,
};

export default GeneralSelectComponent;
